import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { WeeklyReport } from "@/types/WeeklyReport";
import {
  endOfDay,
  format,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  startOfDay,
} from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  FileText,
  Search,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface ReportAnalyticsProps {
  reports: WeeklyReport[];
  loading: boolean;
}

// Helper function to safely format dates
function formatDate(dateStr: string | Date | undefined): string {
  if (!dateStr) return "No date";

  try {
    const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    if (isNaN(date.getTime())) return "Invalid date";
    return format(date, "MMM d, yyyy");
  } catch (error) {
    return "Invalid date";
  }
}

// Helper function to get a valid date from a report
function getValidDate(report: WeeklyReport): Date | null {
  try {
    // Use date first
    if (report.date) {
      const parsed = parseISO(report.date);
      if (isValid(parsed)) {
        return parsed;
      }
    }
    // Fallback or alternative logic if needed (e.g., using dateRange?)
    // Removed createdAt fallback as it doesn't exist
  } catch (error) {
    console.error("Error parsing date:", error);
  }
  return null;
}

export default function ReportAnalytics({
  reports,
  loading,
}: ReportAnalyticsProps) {
  const [statusFilter, setStatusFilter] = useState<"all" | "sent">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReport, setSelectedReport] = useState<WeeklyReport | null>(
    null,
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortField, setSortField] = useState<"date" | "subject" | "createdBy">(
    "date",
  );
  const [isUserFilterExpanded, setIsUserFilterExpanded] = useState(false);
  const [sortUpdateFlag, setSortUpdateFlag] = useState(0);
  const [selectedClient, setSelectedClient] = useState<string>("all");
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [isClientSearchOpen, setIsClientSearchOpen] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // Add click outside handler to close client dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(event.target as Node)
      ) {
        setIsClientSearchOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get unique users from reports
  const uniqueUsers = useMemo(() => {
    const users = new Set(reports.map((report) => report.createdBy));
    return Array.from(users).filter(Boolean);
  }, [reports]);

  // Get unique clients from reports
  const uniqueClients = useMemo(() => {
    const clients = new Set(
      reports
        .filter((report) => report.clientName)
        .map((report) => report.clientName),
    );
    return Array.from(clients).sort();
  }, [reports]);

  // Filter clients based on search
  const filteredClients = useMemo(() => {
    if (!clientSearchQuery && !isClientSearchOpen) return [];
    if (!clientSearchQuery && isClientSearchOpen) return uniqueClients;
    return uniqueClients.filter((client) =>
      client.toLowerCase().includes(clientSearchQuery.toLowerCase()),
    );
  }, [uniqueClients, clientSearchQuery, isClientSearchOpen]);

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery && !isUserFilterExpanded) return [];
    if (!userSearchQuery && isUserFilterExpanded) return uniqueUsers;
    return uniqueUsers.filter((user) =>
      user.toLowerCase().includes(userSearchQuery.toLowerCase()),
    );
  }, [uniqueUsers, userSearchQuery, isUserFilterExpanded]);

  // Calculate stats
  const stats = useMemo(() => {
    const sent = reports.filter((report) => report.sentByEmail === true).length;
    const totalUsers = uniqueUsers.length;
    return { completed: sent, totalUsers };
  }, [reports, uniqueUsers]);

  // Filter reports based on all filters
  const filteredReports = useMemo(() => {
    let filtered = reports;

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((report) => {
        if (statusFilter === "sent") return report.sentByEmail === true;
        return true;
      });
    }

    // User filter
    if (selectedUsers.length > 0) {
      filtered = filtered.filter((report) =>
        selectedUsers.includes(report.createdBy),
      );
    }

    // Date filter
    if (startDate || endDate) {
      filtered = filtered.filter((report) => {
        const reportDate = getValidDate(report);
        if (!reportDate) return false;

        if (startDate && endDate) {
          return (
            isAfter(reportDate, startOfDay(startDate)) &&
            isBefore(reportDate, endOfDay(endDate))
          );
        }
        if (startDate) {
          return isAfter(reportDate, startOfDay(startDate));
        }
        if (endDate) {
          return isBefore(reportDate, endOfDay(endDate));
        }
        return true;
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (report) =>
          report.subject?.toLowerCase().includes(query) ||
          report.createdBy?.toLowerCase().includes(query) ||
          (report.clientName &&
            report.clientName.toLowerCase().includes(query)),
      );
    }

    // Client filter
    if (selectedClient !== "all") {
      filtered = filtered.filter(
        (report) => report.clientName === selectedClient,
      );
    }

    // Sort
    console.log("Resorting data with order:", sortOrder);
    filtered.sort((a, b) => {
      const dateA = getValidDate(a);
      const dateB = getValidDate(b);

      let comparison = 0;
      switch (sortField) {
        case "date":
          // Handle cases where one or both dates are null
          if (dateA && dateB) {
            comparison = dateA.getTime() - dateB.getTime();
          } else if (dateA) {
            comparison = 1; // A has date, B doesn't - A comes after B
          } else if (dateB) {
            comparison = -1; // B has date, A doesn't - B comes after A
          } else {
            comparison = 0; // Neither has a date
          }
          break;
        case "subject":
          comparison = (a.subject || "").localeCompare(b.subject || "");
          break;
        case "createdBy":
          comparison = (a.createdBy || "").localeCompare(b.createdBy || "");
          break;
      }

      return sortOrder === "desc" ? -comparison : comparison;
    });

    return filtered;
  }, [
    reports,
    statusFilter,
    selectedUsers,
    startDate,
    endDate,
    searchQuery,
    sortOrder,
    sortField,
    selectedClient,
  ]);

  const clearFilters = () => {
    setStatusFilter("all");
    setSelectedUsers([]);
    setStartDate(undefined);
    setEndDate(undefined);
    setSearchQuery("");
    setSortOrder("desc");
    setSortField("date");
    setUserSearchQuery("");
    setIsUserFilterExpanded(false);
    setSelectedClient("all");
    setClientSearchQuery("");
    setIsClientSearchOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate statistics only once

  // Update sort order handler
  const handleSortOrderChange = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    setSortUpdateFlag((prev) => prev + 1); // Increment to force refresh
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Completed Reports
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
            <p className="text-xs text-muted-foreground">
              Reports sent by email
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsers}</div>
            <p className="text-xs text-muted-foreground">Users with reports</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Filter reports by status, date, user, or client
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <Label htmlFor="status-filter">Status:</Label>
              <Select
                value={statusFilter}
                onValueChange={(value: "all" | "sent") =>
                  setStatusFilter(value)
                }
              >
                <SelectTrigger id="status-filter" className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Client Filter */}
            <div className="flex items-center space-x-2">
              <Label htmlFor="client-filter">Client:</Label>
              <div className="relative" ref={clientDropdownRef}>
                <Select
                  value={selectedClient}
                  onValueChange={setSelectedClient}
                >
                  <SelectTrigger id="client-filter" className="w-[180px]">
                    <SelectValue placeholder="All clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All clients</SelectItem>
                    {uniqueClients.map((client) => (
                      <SelectItem key={client} value={client}>
                        {client}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* User Filter */}
            <div className="flex items-center space-x-2">
              <Label>Users:</Label>
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setIsUserFilterExpanded(!isUserFilterExpanded)}
                  className="w-[180px] justify-between"
                >
                  {selectedUsers.length > 0
                    ? `${selectedUsers.length} selected`
                    : "All users"}
                  {isUserFilterExpanded ? (
                    <ChevronUp className="ml-2 h-4 w-4" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4" />
                  )}
                </Button>
                {isUserFilterExpanded && (
                  <div className="absolute z-10 mt-1 w-[280px] rounded-md border bg-popover p-4 shadow-md">
                    <div className="space-y-2">
                      <Input
                        placeholder="Search users..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="mb-2"
                      />
                      <div className="max-h-[200px] overflow-y-auto space-y-2">
                        {filteredUsers.map((user) => (
                          <div
                            key={user}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`user-${user}`}
                              checked={selectedUsers.includes(user)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedUsers([...selectedUsers, user]);
                                } else {
                                  setSelectedUsers(
                                    selectedUsers.filter((u) => u !== user),
                                  );
                                }
                              }}
                            />
                            <Label
                              htmlFor={`user-${user}`}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {user}
                            </Label>
                          </div>
                        ))}
                        {filteredUsers.length === 0 && (
                          <p className="text-sm text-muted-foreground">
                            No users found
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Range Filters */}
            <div className="flex items-center space-x-2">
              <Label>Date Range:</Label>
              <Input
                type="date"
                value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
                onChange={(e) =>
                  setStartDate(
                    e.target.value ? new Date(e.target.value) : undefined,
                  )
                }
                className="w-[150px]"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                onChange={(e) =>
                  setEndDate(
                    e.target.value ? new Date(e.target.value) : undefined,
                  )
                }
                className="w-[150px]"
              />
            </div>

            {/* Search */}
            <div className="flex items-center space-x-2 flex-1 min-w-[200px]">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Clear Filters */}
            <Button variant="outline" onClick={clearFilters}>
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reports</CardTitle>
          <CardDescription>
            {filteredReports.length} report
            {filteredReports.length !== 1 ? "s" : ""} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSortField("date");
                        handleSortOrderChange();
                      }}
                      className="h-8 px-2"
                    >
                      Date
                      {sortField === "date" &&
                        (sortOrder === "asc" ? (
                          <ArrowUp className="ml-2 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-2 h-4 w-4" />
                        ))}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSortField("subject");
                        handleSortOrderChange();
                      }}
                      className="h-8 px-2"
                    >
                      Subject
                      {sortField === "subject" &&
                        (sortOrder === "asc" ? (
                          <ArrowUp className="ml-2 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-2 h-4 w-4" />
                        ))}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSortField("createdBy");
                        handleSortOrderChange();
                      }}
                      className="h-8 px-2"
                    >
                      Created By
                      {sortField === "createdBy" &&
                        (sortOrder === "asc" ? (
                          <ArrowUp className="ml-2 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-2 h-4 w-4" />
                        ))}
                    </Button>
                  </TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No reports found matching your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">
                        {formatDate(report.date)}
                      </TableCell>
                      <TableCell>{report.subject || "No subject"}</TableCell>
                      <TableCell>{report.createdBy || "Unknown"}</TableCell>
                      <TableCell>{report.clientName || "-"}</TableCell>
                      <TableCell>
                        <Badge
                          variant={report.sentByEmail ? "default" : "secondary"}
                          className={cn(
                            report.sentByEmail
                              ? "bg-green-100 text-green-800 hover:bg-green-100"
                              : "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                          )}
                        >
                          {report.sentByEmail ? (
                            <>
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Sent
                            </>
                          ) : (
                            <>
                              <Clock className="mr-1 h-3 w-3" />
                              Draft
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedReport(report)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Report Detail Dialog */}
      <Dialog
        open={!!selectedReport}
        onOpenChange={(open) => !open && setSelectedReport(null)}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedReport?.subject || "Report Details"}
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Date
                  </Label>
                  <p className="text-sm">{formatDate(selectedReport.date)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Created By
                  </Label>
                  <p className="text-sm">
                    {selectedReport.createdBy || "Unknown"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Client
                  </Label>
                  <p className="text-sm">{selectedReport.clientName || "-"}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Status
                  </Label>
                  <Badge
                    variant={
                      selectedReport.sentByEmail ? "default" : "secondary"
                    }
                    className={cn(
                      selectedReport.sentByEmail
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800",
                    )}
                  >
                    {selectedReport.sentByEmail ? "Sent" : "Draft"}
                  </Badge>
                </div>
              </div>
              {selectedReport.to && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    To
                  </Label>
                  <p className="text-sm">
                    {Array.isArray(selectedReport.to)
                      ? selectedReport.to.join(", ")
                      : selectedReport.to}
                  </p>
                </div>
              )}
              {selectedReport.cc && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    CC
                  </Label>
                  <p className="text-sm">
                    {Array.isArray(selectedReport.cc)
                      ? selectedReport.cc.join(", ")
                      : selectedReport.cc}
                  </p>
                </div>
              )}
              {selectedReport.bcc && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    BCC
                  </Label>
                  <p className="text-sm">
                    {Array.isArray(selectedReport.bcc)
                      ? selectedReport.bcc.join(", ")
                      : selectedReport.bcc}
                  </p>
                </div>
              )}
              {selectedReport.body && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Body
                  </Label>
                  <div className="mt-2 p-4 border rounded-md bg-muted/50">
                    <pre className="text-sm whitespace-pre-wrap font-sans">
                      {selectedReport.body}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
